/**
 * Generic connected-account row for integration settings sections.
 *
 * One authenticated third-party account rendered as a single row: a provider
 * glyph, an identity line (typically the account email), an optional status
 * dot, and a slot for actions on the right. Any future integration type (a
 * CRM, a ticketing system) reuses this primitive so every integration section
 * shares the same shape. The markup mirrors the connected sign-in method rows
 * in AccountSection.
 *
 * @module components/admin/settings/integrations/ConnectedAccountRow
 */
import React from 'react';

/** Props for a single connected-account row. */
export interface ConnectedAccountRowProps {
  /** Provider glyph rendered on the left. */
  icon: React.ReactNode;
  /** Primary identity line (typically the account email). */
  identity: string;
  /** Optional secondary line under the identity. */
  secondary?: string;
  /**
   * Connection health. `active` shows a success dot; `attention` shows a
   * warning dot (revoked or errored). Omit to hide the dot entirely.
   */
  status?: 'active' | 'attention';
  /** Action controls rendered on the right (a button or buttons). */
  actions: React.ReactNode;
}

/**
 * A single connected third-party account, rendered as a bordered row with the
 * provider glyph and identity on the left and actions on the right.
 */
export const ConnectedAccountRow: React.FC<ConnectedAccountRowProps> = ({
  icon,
  identity,
  secondary,
  status,
  actions,
}) => (
  <div className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
    <div className="flex items-center gap-3 min-w-0">
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <p className="text-sm text-foreground font-medium truncate">{identity}</p>
        {secondary && <p className="text-xs text-muted-foreground truncate">{secondary}</p>}
      </div>
      {status && (
        <span
          data-testid="status-dot"
          aria-hidden="true"
          className={`shrink-0 w-2 h-2 rounded-full ${
            status === 'active' ? 'bg-success' : 'bg-warning'
          }`}
        />
      )}
    </div>
    <div className="shrink-0 ml-3">{actions}</div>
  </div>
);
